import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HardDrive, Download, RotateCcw, Upload } from "lucide-react";
import { useApi, errMsg, ago } from "@/lib/api";

export const BackupsPanel = () => {
  const api = useApi();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const load = useCallback(() => api("get", "/admin/backups").then(setList).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const download = (b) => run(async () => {
    const data = await api("get", `/admin/backups/${b.id}/download`);
    const url = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: "application/json" }));
    Object.assign(document.createElement("a"), { href: url, download: `weekend-edge-backup-${b.created_at.slice(0, 10)}.json` }).click();
    URL.revokeObjectURL(url);
  }, "Backup downloaded");
  const restore = (b) => window.confirm("Restore this backup? Records in the backup are put back over the current ones; anything newer is kept. A safety backup is taken first.") &&
    run(() => api("post", `/admin/backups/${b.id}/restore`), "Backup restored — refresh the page");
  const upload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !window.confirm(`Restore from ${f.name}? Records in the backup are put back over the current ones; anything newer is kept. A safety backup is taken first.`)) return;
    run(async () => api("post", "/admin/restore-file", JSON.parse(await f.text())), "Backup restored — refresh the page");
  };

  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/5 p-5" data-testid="admin-backups-panel">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="font-display text-lg font-bold flex items-center gap-2 mr-auto"><HardDrive className="w-4 h-4 text-blue-400" />Backups</h2>
        <button data-testid="backup-now-button" disabled={busy} onClick={() => run(() => api("post", "/admin/backups"), "Backup saved")} className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs">Back up now</button>
        <button data-testid="backup-upload-button" disabled={busy} onClick={() => file.current?.click()} className="p-1.5 rounded-full hover:bg-white/10" aria-label="restore from file"><Upload className="w-4 h-4" /></button>
        <input ref={file} type="file" accept="application/json" className="hidden" onChange={upload} data-testid="backup-file-input" />
      </div>
      <p className="text-xs text-slate-500 mb-2">Automatic backup every night at 2am Brisbane time. The last 7 are kept. Download one regularly to keep your own copy.</p>
      {list.map((b) => (
        <div key={b.id} className="flex items-center gap-2 py-1.5 border-t border-white/5 text-xs text-slate-400">
          <span className="mr-auto">{ago(b.created_at)} · {b.trigger} · {b.counts?.meetings ?? 0} meetings</span>
          <button data-testid={`backup-download-${b.id}`} onClick={() => download(b)} disabled={busy} className="p-1 hover:text-white" aria-label="download"><Download className="w-3.5 h-3.5" /></button>
          <button data-testid={`backup-restore-${b.id}`} onClick={() => restore(b)} disabled={busy} className="p-1 hover:text-amber-300" aria-label="restore"><RotateCcw className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </section>
  );
};
